import React, { useEffect, useState } from 'react';
import type { NPC, DialogOption } from '../../../../shared/NPCData';
import { TomeButton } from '../../primitives/tome';
import type { QuestCompletionNotice } from '../../contexts/InteractionContext';
import { NpcCompletionNotice } from './NpcCompletionNotice';
import { NpcRichText } from './NpcRichText';
import { NpcCompletedRibbon } from './NpcCompletedRibbon';

interface DialogueViewProps {
  npc: NPC;
  npcTypeInfo: { color: string; icon: string; name: string };
  onDialogSelect?: (option: DialogOption) => void;
  /** A quest just turned in here: its completion line replaces the greeting. */
  completion?: QuestCompletionNotice | null;
}

/** Authored dialogue and original actions; no invented branching or service commands. */
export const DialogueView: React.FC<DialogueViewProps> = ({ npc, onDialogSelect, completion }) => {
  const [activeSpeech, setActiveSpeech] = useState<string | null>(null);
  const [activeOptions, setActiveOptions] = useState<DialogOption[] | null>(null);

  // Reset on a real transition (NPC or completed quest), not on object identity:
  // the overlay rebuilds an equivalent completion object every render (R2-02).
  const completionKey = completion ? `${completion.npcId}:${completion.questId}` : null;
  useEffect(() => {
    setActiveSpeech(null);
    setActiveOptions(null);
  }, [npc.id, completionKey]);

  const displayedGreeting = activeSpeech ?? npc.dialog.greeting;
  const displayedOptions = activeOptions ?? npc.dialog.options;

  const handleSelect = (option: DialogOption) => {
    if (option.action === 'back') {
      setActiveSpeech(null);
      setActiveOptions(null);
      return;
    }
    if (option.speech) {
      setActiveSpeech(option.speech);
      setActiveOptions(option.subOptions ?? [
        { text: 'Back', action: 'back' },
      ]);
    }
    // Service choices carry `action` or a server-projected `ferryAction` (R2-01).
    if ((option.action && option.action !== 'back') || option.ferryAction) {
      onDialogSelect?.(option);
    }
  };

  return (
    <div className="npc-dialogue">
      {activeSpeech ? (
        <blockquote className="npc-dialogue-greeting"><NpcRichText text={activeSpeech} /></blockquote>
      ) : completion ? (
        <NpcCompletionNotice completion={completion} />
      ) : (
        <>
          <NpcCompletedRibbon quests={npc.completedQuests} />
          <blockquote className="npc-dialogue-greeting"><NpcRichText text={displayedGreeting} /></blockquote>
        </>
      )}
      {displayedOptions.length > 0 && <div className="npc-dialogue-options" aria-label="Dialogue options">
        {displayedOptions.map((option, index) => <TomeButton key={`${option.action ?? option.text}-${index}`}
          tone="secondary" fullWidth disabled={!onDialogSelect && !option.speech && option.action !== 'back'}
          onClick={() => handleSelect(option)}>
          {option.text}
        </TomeButton>)}
      </div>}
    </div>
  );
};
